import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-location'
import { useQuery } from 'react-query'
import { useMediaQuery } from '@react-hook/media-query'
import moment from 'moment'
// Store
import { useStore } from 'TourApp/store'
import { events, posttour } from 'shared/analytics'

// Icons
import { ReactComponent as CalendarIcon } from 'shared/assets/icons/calendar.svg'
import { ReactComponent as UserHomeIcon } from 'shared/assets/icons/userHome.svg'
import { ReactComponent as BuildingIcon } from 'shared/assets/icons/building.svg'
// Services
import { getTourData, upsertToursByUUID } from 'shared/services/tour.service'
// Components
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
// Icons
import Checkbox from 'shared/components/Checkbox'
import TermsModal from 'shared/components/TermsModal'
import { hasVideos, isApartmentApp, isGalleryAvailable } from 'shared/helpers/general'

function scrollToIntroBottom() {
  setTimeout(() => {
    const el = document.getElementById('intro')
    el.scrollTo(0, el.scrollHeight)
  })
}

export default function IntroContent({ tourCode }) {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const { setTourData, tourData, setSelectedApartmentId } = useStore()
  const navigate = useNavigate()
  const { isLoading, data, isSuccess } = useQuery('tourData', () => getTourData(tourCode))
  const [termsModalShown, setTermsModalShown] = useState(false)
  const [termsConsent, setTermsConsent] = useState(undefined)

  useEffect(() => {
    setTermsConsent(Boolean(Number(localStorage.getItem('termsConsent'))))
  }, [])

  useEffect(() => {
    if (termsConsent !== undefined) {
      localStorage.setItem('termsConsent', termsConsent ? 1 : 0)
    }
  }, [termsConsent])

  useEffect(() => {
    if (data) {
      const tour = JSON.parse(JSON.stringify(data.data.tours_by_code))
      setTourData(tour)
      if (tour.invitation_status === 'pending') {
        upsertToursByUUID({
          uuid: tour.uuid,
          appUserFirstName: tour.app_user_first_name,
          appUserLastName: tour.app_user_last_name,
          appUserEmail: tour.app_user_email,
          invitationStatus: 'accepted',
        })
      }
      if (isMobile) {
        scrollToIntroBottom()
      }
    }
  }, [data, setTourData])

  useEffect(() => {
    if (isMobile) {
      scrollToIntroBottom()
    }
  }, [])

  useEffect(() => {
    if (tourData && isApartmentApp()) {
      onContinue()
    }
  }, [tourData])

  useEffect(() => {
    if (!data?.data?.tours_by_code && isSuccess) posttour(events.tccf, null, true)
    if (data?.data?.tours_by_code) posttour(events.tccs, null, true)
  }, [data, isSuccess])

  useEffect(() => {
    if (tourData) posttour(events.wp, null, true)
  }, [tourData])

  function onContinue() {
    setSelectedApartmentId(tourData.units[0].unit.id)
    posttour(events.wcc, null, true)
    posttour(events.mas)
    if (isGalleryAvailable(tourData)) {
      navigate({ to: '/gallery' })
      return
    }
    if (hasVideos(tourData.units[0].unit)) {
      navigate({ to: '/videos' })
      return
    }
    alert('Error, please try again later.')
  }

  function openTermsModal(e) {
    e.preventDefault()
    setTermsModalShown(true)
    posttour(events.wtaco, null, true)
  }

  function onTermsConsentChange() {
    const targetConsentState = !termsConsent
    setTermsConsent(targetConsentState)
    posttour(targetConsentState ? events.wdc : events.wdu, null, true)
  }

  function renderGreeting() {
    const { app_user_first_name, app_user_last_name } = tourData
    if (app_user_first_name && app_user_last_name) {
      return (
        <>
          Dear {app_user_first_name} {app_user_last_name},
          <br className="hidden lg:inline-block" />{' '}
          <span className="font-normal lg:font-medium">thanks for touring at:</span>
        </>
      )
    } else {
      return <span className="font-normal lg:font-medium">Thanks for touring at:</span>
    }
  }

  return (
    <>
      <div className="flex flex-col justify-between items-start drop-shadow-md w-screen p-[24px] lg:w-[445px] lg:min-h-[576px] lg:px-[72px] lg:py-[60px] lg:mb-[75px] lg:ml-[75px] bg-white">
        {isLoading && (
          <div className="w-full flex justify-center items-center">
            <Loader />
          </div>
        )}
        {!tourData && !isLoading && (
          <div className="font-semibold text-[32px] capitalize lg:text-[42px] w-full text-center">Tour not found !</div>
        )}
        {tourData && (
          <>
            <div className="flex flex-col items-start">
              <div className="leading-[21px] font-semibold lg:font-medium">{renderGreeting()}</div>
              <div className="flex flex-col justify-center mt-[12px] lg:mt-[18px]">
                <h1 className="font-semibold text-[28px] leading-[34px] capitalize lg:text-[40px] lg:leading-[48px] lg:font-bold">
                  {tourData.units[0].unit.unit_type.property.property_name}
                </h1>
                <div className="text-[12px] leading-[15px] mt-[8px] lg:text-[14px] lg:leading-[17px] text-[#5F6787] tracking-wider uppercase font-medium lg:font-semibold">
                  {tourData.units[0].unit.unit_type.property.street}
                </div>
              </div>
              <div className="flex flex-col font-medium mt-[27px] lg:mt-[56px]">
                {[
                  {
                    Icon: CalendarIcon,
                    text: moment(tourData.start_time).format('L, LT'),
                  },
                  {
                    Icon: UserHomeIcon,
                    text: `${tourData.agent.first_name} ${tourData.agent.last_name}`,
                  },
                  {
                    Icon: BuildingIcon,
                    text: tourData.units[0].unit.unit_type.property.real_estate_company.company_name,
                  },
                ].map((i) => (
                  <div className="flex items-center mt-[12px] first:mt-0" key={i.text}>
                    {<i.Icon className="w-[23px] mr-2 lg:mr-4" />}
                    <div className="text-[14px] lg:text-[16px] leading-[21px] font-normal">{i.text}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-[20px] lg:mt-[80px] w-full flex flex-col landscape:pb-[30px] lg:landscape:pb-0">
              <div className="bg-[#C7CCD9] h-[1px] w-full lg:hidden"></div>
              <div className="text-[14px] leading-[21px] flex items-center mt-[15px] lg:mt-0">
                <Checkbox onChange={onTermsConsentChange} checked={termsConsent} />
                <div className="ml-2">
                  Agree to{' '}
                  <a href="!#" onClick={openTermsModal} className="text-[#1A75FF] underline">
                    Terms & Conditions
                  </a>
                </div>
              </div>
              <Button onClick={onContinue} className="w-full lg:w-[210px] mt-[18px]" disabled={!termsConsent}>
                Continue
              </Button>
            </div>
          </>
        )}
      </div>
      {termsModalShown && <TermsModal onClose={setTermsModalShown} />}
    </>
  )
}
